/**
 * The one source of credit wording (M4S-5895, docs/pdlc/credits-ui.md §7 "One vocabulary").
 *
 * Every screen that says something about credits takes its words from here: the chat's pause
 * banners and placeholder, the Credits tab, the CS org page, the fleet, CSV headers, the CS
 * action modals and any feature that shows its own cost. A reason's copy lives only in
 * `pauseCopy`; `read-denial.ts` maps a body to a reason and holds no copy.
 *
 * `credit-vocabulary.guard.test.ts` fails on the retired words ("monthly compute cap", "Annual
 * credit pool", "account team", "account manager", "soft cap", "overages billed", "Unlimited")
 * in the credits files, and on a second declaration of any function exported here.
 *
 * Pure: no React, no fetching. Dates are ISO strings (`YYYY-MM-DD` or a full timestamp) and are
 * shown as "Mar 3, 2027".
 */

import { LEDGER_MINUS, NEGATIVE_BALANCE_ANNOTATION } from '@/@credit-consumption/types/constants';
import { formatContractDate } from '@/@credit-consumption/utils/formatContractDate';
import { formatCredits, roundCredits } from '@/@credit-consumption/utils/formatCredits';

import type { SpendCheckResult, SpendPurpose } from '@/@credit-consumption/types/spend-check';

import type { CreditGateReason } from './read-denial';

// ---------------------------------------------------------------------------------------------
// The contact
// ---------------------------------------------------------------------------------------------

/**
 * The one contact for credit questions.
 *
 * TODO(OQ8): the contact role (and the trial users' contact, likely Sales with a link) is open
 * with Yvette Gonzalez and Aaron Wepler. Until it is decided every screen names this one, so the
 * answer is a one-line change here. The address is the one the bulk-credits modal already used.
 */
export const CREDITS_CONTACT = {
    name: 'MFour Customer Success',
    email: 'success@mfour.com',
} as const;

/** "MFour Customer Success (success@mfour.com)". */
export function contactLine(): string {
    return `${CREDITS_CONTACT.name} (${CREDITS_CONTACT.email})`;
}

/** Where a person goes for more credits: members to their org admin first (§7 "Contact"). */
export function askForCreditsLine(ctx: { orgName?: string; isOrgAdmin: boolean }): string {
    if (ctx.isOrgAdmin) return `Contact ${contactLine()}.`;
    const admin = ctx.orgName ? `${ctx.orgName}'s admin` : 'your admin';
    return `Ask ${admin}, or contact ${contactLine()}.`;
}

// ---------------------------------------------------------------------------------------------
// Terms
// ---------------------------------------------------------------------------------------------

/** Fixed words, one per concept (§7 table). */
export const CREDIT_TERMS = {
    balance: 'Balance',
    provisional: 'Provisional',
    heldForFielding: 'Held for fielding',
    noCreditLimit: 'No credit limit',
    noCreditLimitInternal: 'No credit limit (MFour)',
    belowZero: NEGATIVE_BALANCE_ANNOTATION,
    staffOnBehalf: 'MFour (on your behalf)',
    contractCredits: 'Contract credits',
    creditPack: 'Credit pack',
    trialCredits: 'Trial credits',
    reportDelivered: 'Report delivered by MFour',
    surveyCompletesBooked: 'Survey completes (booked)',
    unattributed: 'DANI work not tied to one conversation',
    unattributedTooltip:
        "DANI-built reports and the time DANI's warehouse stays ready between questions",
    reSplit: 'Cost re-split across conversations (total unchanged)',
    trialEndedAtSignUp: 'Trial credits ended at sign-up',
    seeCredits: 'See credits',
    usageTiming: 'Usage appears within a few hours of use',
    sourceLedger: 'Shown from the new credits balance',
    sourceLegacy: 'Shown from the previous calculation',
} as const;

/** Every pause banner ends with this link (§7 "Chat"). */
export const SEE_CREDITS_LINK = {
    label: CREDIT_TERMS.seeCredits,
    href: '/account/credits',
} as const;

// ---------------------------------------------------------------------------------------------
// Formatting helpers (shared by the copy below)
// ---------------------------------------------------------------------------------------------

/** "Mar 3, 2027"; empty for a missing date. */
export function creditDate(value: string | null | undefined): string {
    return formatContractDate(value);
}

/** "credit" when the amount shown reads 1, else "credits" (the unit follows the rounded figure). */
function creditUnit(n: number): string {
    return roundCredits(Math.abs(n)) === 1 ? 'credit' : 'credits';
}

/** "12,345 credits", "1 credit", "−40 credits" (U+2212 for a negative amount). */
export function creditsAmount(n: number): string {
    const body = formatCredits(Math.abs(n));
    const sign = n < 0 && roundCredits(Math.abs(n)) !== 0 ? LEDGER_MINUS : '';
    return `${sign}${body} ${creditUnit(n)}`;
}

// ---------------------------------------------------------------------------------------------
// Balance lines
// ---------------------------------------------------------------------------------------------

/** "Balance for Acme" (credits are always the user's own org's; pooling is off). */
export function balanceForLine(orgName: string): string {
    return `${CREDIT_TERMS.balance} for ${orgName}`;
}

/** "Usage counted up to Oct 26, 2026, 3:00 PM"; the caller formats the time. */
export function usageCountedUpTo(formattedTime: string): string {
    return `Usage counted up to ${formattedTime}`;
}

/** "Includes 210 provisional credits" and its tooltip. */
export function provisionalLine(credits: number, settlesOn: string | null | undefined) {
    return {
        line: `Includes ${formatCredits(credits)} provisional ${creditUnit(credits)}`,
        tooltip: settlesOn
            ? `Counted within a few hours of use. May change slightly until ${creditDate(settlesOn)}.`
            : 'Counted within a few hours of use. May change slightly until the month settles.',
    };
}

/** "About 40 credits of recent usage not counted yet". */
export function liveEstimateLine(credits: number): string {
    return `About ${creditsAmount(credits)} of recent usage not counted yet`;
}

/** "1,200 held for fielding". */
export function heldLine(credits: number): string {
    return `${formatCredits(credits)} ${CREDIT_TERMS.heldForFielding.toLowerCase()}`;
}

/** D21: "40 credits over. They come out of your next credits." */
export function belowZeroLine(balance: number): string {
    return `${creditsAmount(Math.abs(balance))} over. They come out of your next credits.`;
}

/** "No credit limit", or "No credit limit (MFour)" for MFour's own orgs. */
export function noLimitLabel(isInternalOrg: boolean): string {
    return isInternalOrg ? CREDIT_TERMS.noCreditLimitInternal : CREDIT_TERMS.noCreditLimit;
}

/** "Shown from the new credits balance" / "Shown from the previous calculation" (staff only). */
export function sourceCaption(reader: 'ledger' | 'legacy'): string {
    return reader === 'ledger' ? CREDIT_TERMS.sourceLedger : CREDIT_TERMS.sourceLegacy;
}

// ---------------------------------------------------------------------------------------------
// End date (one per page)
// ---------------------------------------------------------------------------------------------

/**
 * `grant`: an org with no contract or trial whose credits all come from packs or CS adds; its end
 * date is when the last of them ends (`CreditBalance.endDateKind`, §2.4).
 */
export type EndDateKind = 'contract' | 'trial' | 'grant' | 'none';

/** "Contract year ends {date}", "Trial ends {date}", "Credits end {date}" or "No end date". */
export function endDateLabel(kind: EndDateKind, date?: string | null): string {
    if (kind === 'none' || !date) return 'No end date';
    if (kind === 'trial') return `Trial ends ${creditDate(date)}`;
    if (kind === 'grant') return `Credits end ${creditDate(date)}`;
    return `Contract year ends ${creditDate(date)}`;
}

// ---------------------------------------------------------------------------------------------
// Purposes and rows
// ---------------------------------------------------------------------------------------------

/** `pdlc_credit_ledger.kind` (§2.1). */
export type LedgerKind =
    | 'grant'
    | 'opening_balance'
    | 'usage'
    | 'expiry'
    | 'adjustment'
    | 'transfer'
    | 'correction';

/** `pdlc_credit_ledger.purpose` (§2.1). */
export type LedgerPurpose =
    | 'chat'
    | 'unattributed'
    | 'report'
    | 'feasibility'
    | 'survey_build'
    | 'fielding'
    | 'contract'
    | 'pack'
    | 'booking'
    | 'trial_grant'
    | 'adjustment'
    | 'transfer'
    | 'opening_balance'
    | 'correction'
    | 'expiry'
    /** M4S-6017: a Claude or OpenAI call charged on its own (`llm:` rows). */
    | 'model_call';

/** The usage purposes a usage bar or filter can show (§7 "Purposes"). */
export type UsagePurpose =
    | 'chat'
    | 'unattributed'
    | 'report'
    | 'feasibility'
    | 'survey_build'
    | 'fielding'
    | 'model_call';

const PURPOSE_LABELS: Record<UsagePurpose, string> = {
    chat: 'Chat',
    unattributed: CREDIT_TERMS.unattributed,
    feasibility: 'Feasibility',
    survey_build: 'Survey build',
    fielding: 'Fielding',
    // M4S-6017: Claude and OpenAI calls from Studio's AI features, charged on their own
    model_call: 'AI features',
    // Reserved until the Agents team records each report run's usage (§2.5, M4S-5791).
    report: 'Report',
};

/** The purpose's on-screen name; also the CSV column header. */
export function purposeLabel(purpose: UsagePurpose): string {
    return PURPOSE_LABELS[purpose];
}

/** Context a row label needs; every field is optional and only the row's own kind reads it. */
export interface RowLabelContext {
    /** Grants: "Contract credits" / "Credit pack" / "Trial credits" read from the purpose. */
    grantLabel?: string;
    /** CS rows: the only reason text a client sees (`client_reason`). */
    clientReason?: string | null;
    /** Corrections: the ISO date of the month being corrected. */
    correctsMonth?: string | null;
    /** A correction that only re-split a month's cost across conversations. */
    reSplit?: boolean;
    /** The opening-balance true-up row. */
    trueUp?: boolean;
    /** Opening rows: original amount, amount left and the T0 date. */
    opening?: { label: string; left: number; original: number; asOf: string };
    /** Expiry rows: the grant that expired; `conversion` for a trial ended at sign-up (D11). */
    expiredGrant?: string;
    conversion?: boolean;
    /** Fielding draws: the survey's name. */
    surveyLabel?: string;
}

function monthYear(value: string | null | undefined): string {
    if (!value) return '';
    const formatted = creditDate(value); // "Oct 1, 2026"
    const match = /^([A-Z][a-z]{2}) \d{1,2}, (\d{4})$/.exec(formatted);
    return match ? `${match[1]} ${match[2]}` : formatted;
}

function withReason(base: string, reason: string | null | undefined): string {
    return reason ? `${base} · ${reason}` : base;
}

const GRANT_PURPOSE_LABELS: Partial<Record<LedgerPurpose, string>> = {
    contract: CREDIT_TERMS.contractCredits,
    pack: CREDIT_TERMS.creditPack,
    trial_grant: CREDIT_TERMS.trialCredits,
};

/**
 * One activity row's "What for" text (§7 table). The client never sees a counterparty org,
 * an actor email or an internal reason here (D12): only `clientReason`.
 */
export function rowLabel(
    kind: LedgerKind,
    purpose: LedgerPurpose,
    ctx: RowLabelContext = {},
): string {
    switch (kind) {
        case 'opening_balance': {
            const o = ctx.opening;
            if (!o) return 'Opening balance';
            return `${o.label} · ${formatCredits(o.left)} left of ${formatCredits(o.original)} on ${creditDate(o.asOf)}`;
        }
        case 'grant':
            if (purpose === 'adjustment')
                return withReason('Adjustment by MFour', ctx.clientReason);
            if (purpose === 'transfer') return withReason('Transfer by MFour', ctx.clientReason);
            return ctx.grantLabel ?? GRANT_PURPOSE_LABELS[purpose] ?? 'Credits added';
        case 'adjustment':
            return withReason('Adjustment by MFour', ctx.clientReason);
        case 'transfer':
            return withReason('Transfer by MFour', ctx.clientReason);
        case 'expiry':
            if (ctx.conversion) return CREDIT_TERMS.trialEndedAtSignUp;
            return ctx.expiredGrant ? `Expired unused · ${ctx.expiredGrant}` : 'Expired unused';
        case 'correction': {
            const month = monthYear(ctx.correctsMonth);
            if (ctx.trueUp) {
                const mon = month.split(' ')[0];
                return mon
                    ? `Final ${mon} figure after the switch to the new balance`
                    : 'Final figure after the switch to the new balance';
            }
            if (ctx.reSplit) return CREDIT_TERMS.reSplit;
            return month ? `Correction to ${month} usage` : 'Correction';
        }
        case 'usage':
            if (purpose === 'booking') return ctx.grantLabel ?? CREDIT_TERMS.reportDelivered;
            if (purpose === 'fielding' && ctx.surveyLabel) return `Fielding · ${ctx.surveyLabel}`;
            if (
                purpose === 'chat' ||
                purpose === 'unattributed' ||
                purpose === 'report' ||
                purpose === 'feasibility' ||
                purpose === 'survey_build' ||
                purpose === 'fielding' ||
                purpose === 'model_call'
            ) {
                return purposeLabel(purpose);
            }
            return 'Usage';
        default: {
            const unreachable: never = kind;
            return String(unreachable);
        }
    }
}

// ---------------------------------------------------------------------------------------------
// The client Credits tab on the new balance (§7 blocks 1–10, plan step 26)
// ---------------------------------------------------------------------------------------------

/** Fixed words on the client Credits tab. */
export const CREDITS_TAB_TERMS = {
    grantsHeading: 'Where your credits came from',
    fieldingHeading: 'Fielding',
    usageHeading: 'Usage',
    activityHeading: 'Activity',
    priceListHeading: 'What things cost',
    downloadActivity: 'Download activity (CSV)',
    downloadFailed: "The activity download didn't work. Try again in a few minutes.",
    questions: `Questions about your credits? ${contactLine()}`,
    noCreditsYet: 'This account has no credits yet.',
    freemium: 'Credits come with a Studio plan or trial.',
    noUsage: 'No usage in this period.',
    noActivity: 'No activity in this period.',
    loadFailed: "Credits couldn't be loaded. Try again in a few minutes.",
    provisionalOnly: 'Provisional only',
    allPurposes: 'All purposes',
    everyone: 'Everyone',
    mfour: 'MFour',
    included: 'Included',
    noChange: 'No change to balance',
    settled: 'Settled',
} as const;

/** The usage periods a reader can pick (§7 block 7). */
export const USAGE_PERIOD_LABELS = {
    thisMonth: 'This month',
    lastMonth: 'Last month',
    last3Months: 'Last 3 months',
    last12Months: 'Last 12 months',
} as const;

/** Activity's columns (§7 block 8). */
export const ACTIVITY_COLUMNS = {
    date: 'Date',
    who: 'Who',
    whatFor: 'What for',
    credits: 'Credits',
    status: 'Status',
} as const;

/** D13: the strip staff see on a client's Credits tab under View-as. */
export function viewAsCreditsStrip(orgName: string | null | undefined): string {
    return `Viewing credits for ${orgName?.trim() || 'this client'} as its admins see them (read-only)`;
}

/** D25: a grant's last day is the UTC date of the instant just before it expires. */
function lastDayBefore(expiresAt: string): string {
    const t = Date.parse(expiresAt);
    if (Number.isNaN(t)) return expiresAt;
    return new Date(t - 1).toISOString().slice(0, 10);
}

/** "Contract credits · 6,000 left of 10,000 · ends Jun 17, 2027" (§7 block 5). */
export function grantListLine(grant: {
    label: string;
    remaining: number;
    originalCredits: number;
    expiresAt: string | null;
}): string {
    const base = `${grant.label} · ${formatCredits(grant.remaining)} left of ${formatCredits(grant.originalCredits)}`;
    return grant.expiresAt ? `${base} · ends ${creditDate(lastDayBefore(grant.expiresAt))}` : base;
}

/** "1,200 credits expire on Dec 31, 2026 unless used". */
export function expiringCreditsLine(credits: number, on: string): string {
    return `${creditsAmount(credits)} expire on ${creditDate(on)} unless used`;
}

/** One fielding hold (§7 block 6): open holds show what is held now, closed ones what came back. */
export function fieldingHoldLine(hold: {
    label: string | null;
    status: 'open' | 'closed' | 'expired';
    heldNow: number;
    unitsDrawn: number;
    unitCredits: number | null;
    holdUntil: string;
    creditsBack: number | null;
    /** Completes past the held count, never charged (FT-5); absent on an older API. */
    unitsAbsorbed?: number;
}): string {
    const survey = hold.label?.trim() || 'Survey';
    const absorbed = absorbedCompletesLine(hold.unitsAbsorbed ?? 0);
    if (hold.status !== 'open') {
        const closed = `${survey} · Closed · ${creditsAmount(hold.creditsBack ?? 0)} back`;
        return absorbed ? `${closed} · ${absorbed}` : closed;
    }
    const parts = [
        `${formatCredits(hold.heldNow)} held now`,
        `${hold.unitsDrawn.toLocaleString()} ${hold.unitsDrawn === 1 ? 'complete' : 'completes'} drawn`,
    ];
    if (absorbed) parts.push(absorbed);
    if (hold.unitCredits !== null) parts.push(`${formatCredits(hold.unitCredits)} per complete`);
    parts.push(`held until ${creditDate(hold.holdUntil)}`);
    return `${survey} · ${parts.join(' · ')}`;
}

/** "12 completes past the hold, not charged" (§2.8, FT-5); empty when none were absorbed. */
export function absorbedCompletesLine(units: number): string {
    if (!(units > 0)) return '';
    return `${units.toLocaleString()} ${units === 1 ? 'complete' : 'completes'} past the hold, not charged`;
}

/** A fielding hold line in Activity; it never changes the balance (§2.8). */
export function holdEventLabel(
    event: 'held' | 'released' | 'expired',
    label: string | null,
): string {
    const survey = label?.trim() ? ` · ${label.trim()}` : '';
    if (event === 'held') return `${CREDIT_TERMS.heldForFielding}${survey}`;
    if (event === 'released') return `Fielding closed, unused credits back${survey}`;
    return `Fielding hold ended${survey}`;
}

/** "Oct 2026 usage settled: +3.2 credits across 41 hours" (corrections after month end). */
export function monthSettledLabel(
    month: string | null | undefined,
    credits: number,
    hours: number,
): string {
    const sign = credits > 0 && roundCredits(credits) !== 0 ? '+' : '';
    const label = monthYear(month) || 'Earlier';
    return `${label} usage settled: ${sign}${creditsAmount(credits)} across ${hours.toLocaleString()} ${hours === 1 ? 'hour' : 'hours'}`;
}

export type SplitPart =
    | 'compute_while_queries_ran'
    | 'warehouse_kept_ready'
    | 'language_model'
    | 'warehouse_token_share';

const SPLIT_PART_LABELS: Record<SplitPart, string> = {
    compute_while_queries_ran: 'Compute while queries ran',
    warehouse_kept_ready: 'Share of warehouse time kept ready',
    language_model: 'Language model',
    warehouse_token_share: 'Share of warehouse time',
};

/** The D8 split of a conversation's cost (owner and admins only). */
export function splitPartLabel(part: SplitPart): string {
    return SPLIT_PART_LABELS[part];
}

/** Activity's Status column. */
export function activityStatusLabel(status: 'provisional' | 'settled' | 'no_change'): string {
    if (status === 'provisional') return CREDIT_TERMS.provisional;
    if (status === 'no_change') return CREDITS_TAB_TERMS.noChange;
    return CREDITS_TAB_TERMS.settled;
}

/**
 * Activity's Who column for a client: a person's name or email, "MFour" for a CS row or staff
 * usage, "MFour (on your behalf)" for View-as usage (D22), and nothing for the system.
 */
export function activityWhoLabel(who: {
    kind: 'person' | 'mfour' | 'mfour_on_behalf' | 'system';
    name: string | null;
    email: string | null;
}): string {
    switch (who.kind) {
        case 'person':
            return who.name?.trim() || who.email || '';
        case 'mfour':
            return CREDITS_TAB_TERMS.mfour;
        case 'mfour_on_behalf':
            return CREDIT_TERMS.staffOnBehalf;
        default:
            return '';
    }
}

/** D6: a member's usage beside the org's: "You used 820 · Acme used 4,310 in total". */
export function memberUsageLine(mine: number, orgName: string, orgTotal: number): string {
    return `You used ${formatCredits(mine)} · ${orgName} used ${formatCredits(orgTotal)} in total`;
}

/** "Before Oct 1, 2026: 1,200 credits (DANI 1,000 · reports 200)", from the opening rows. */
export function beforeT0UsageLine(before: {
    t0: string;
    dani: number;
    reports: number;
    surveys: number;
}): string {
    const parts: string[] = [];
    if (before.dani) parts.push(`DANI ${formatCredits(before.dani)}`);
    if (before.reports) parts.push(`reports ${formatCredits(before.reports)}`);
    if (before.surveys) parts.push(`survey completes ${formatCredits(before.surveys)}`);
    const total = before.dani + before.reports + before.surveys;
    const detail = parts.length > 1 ? ` (${parts.join(' · ')})` : '';
    return `Before ${creditDate(before.t0)}: ${creditsAmount(total)}${detail}`;
}

/** "3 conversations" beside a person's usage (admins). */
export function conversationsCount(n: number): string {
    return `${n.toLocaleString()} ${n === 1 ? 'conversation' : 'conversations'}`;
}

/** "Earlier activity (before Oct 1, 2026)". */
export function earlierActivityHeading(t0: string): string {
    return `Earlier activity (before ${creditDate(t0)})`;
}

/**
 * "A DANI answer here averages about 3.2 credits", or MFour's typical figure when the org has
 * none yet; null when there is no figure at all.
 */
export function perAnswerLine(perAnswer: {
    credits: number | null;
    source: 'org' | 'mfour_typical' | null;
}): string | null {
    if (perAnswer.credits === null || perAnswer.source === null) return null;
    return perAnswer.source === 'org'
        ? `A DANI answer here averages about ${creditsAmount(perAnswer.credits)}`
        : `A DANI answer typically uses about ${creditsAmount(perAnswer.credits)}`;
}

/** Trials: "About 40 answers left at that rate". */
export function answersLeftLine(answers: number): string {
    return `About ${answers.toLocaleString()} ${answers === 1 ? 'answer' : 'answers'} left at that rate`;
}

/** D26: MFour staff on MFour's own orgs only; a client user never has a limit. */
export function ownLimitLine(limit: {
    monthlyLimit: number | null;
    monthToDate: number;
}): string | null {
    if (limit.monthlyLimit === null) return null;
    return `Your monthly limit: ${creditsAmount(limit.monthlyLimit)} · ${formatCredits(limit.monthToDate)} used this month`;
}

/**
 * "What things cost" (§7 block 9). No numeric DANI price until OQ15b; survey completes only
 * where fielding is live for the org. The self-service report rate is never listed.
 */
export function priceListLines(ctx: { perAnswer: string | null; fieldingLive: boolean }): string[] {
    const dani = ctx.perAnswer
        ? `DANI: charged by the compute each answer uses. ${ctx.perAnswer}.`
        : 'DANI: charged by the compute each answer uses.';
    const lines = [
        dani,
        'Reports built by DANI: included in DANI usage',
        'Reports delivered by MFour: priced in your contract',
    ];
    if (ctx.fieldingLive) lines.push('Survey completes: priced per survey at launch');
    return lines;
}

/** The usage bar for bookings, which are not one of the purposes a person picks. */
export const BOOKINGS_USAGE_LABEL = 'Delivered by MFour (booked)';

// ---------------------------------------------------------------------------------------------
// Pauses and refusals
// ---------------------------------------------------------------------------------------------

/**
 * Every reason a person can be shown (§2.7). The gate's reasons today (`CreditGateReason`,
 * parsed by `read-denial.ts`) are a subset, checked at compile time below; the trial and plan
 * reasons arrive with `decideSpend`. `LEDGER_NOT_LIVE` is shown to staff only (a move refused
 * because one org is not on the new balance yet). `already_settled` and `released` are service
 * replies and are never shown.
 */
export const PAUSE_REASONS = [
    'no_credits',
    'insufficient_for_action',
    'user_spend_limit_exceeded',
    'license_expired',
    'trial_ended',
    'trial_not_started',
    'not_entitled',
    // Legacy orgs only, until the cleanup PR.
    'monthly_cap_exceeded',
    'annual_pool_exceeded',
    // Staff only.
    'LEDGER_NOT_LIVE',
] as const;

export type PauseReason = (typeof PAUSE_REASONS)[number];

// Compile-time: every reason the gate sends today has copy here.
type AssertSubset<T extends PauseReason> = T;
export type GateReasonsHaveCopy = AssertSubset<CreditGateReason>;

/**
 * What MFour staff at their own per-person limit are told to do (D26: limits are for MFour's own
 * orgs only, to keep staff inference spend in check; clients have none).
 */
export const STAFF_LIMIT_NEXT_STEP =
    'Per-person limits apply to MFour staff only; ask a credits admin to raise yours.';

/** Who is reading: a client org admin, a client member, or MFour staff (the CS page). */
export type CreditAudience = 'admin' | 'member' | 'staff';

export interface PauseContext {
    audience: CreditAudience;
    orgName?: string;
    /** Legacy monthly cap and per-person limits: when they reset. */
    resetDate?: string | null;
    /** Plan, contract or trial end (license_expired, trial_ended) or trial start (trial_not_started). */
    date?: string | null;
    available?: number;
    needed?: number;
    /** Per-person limit (user_spend_limit_exceeded; MFour's own orgs only, D26). */
    limit?: number;
}

export interface PauseCopy {
    title: string;
    body: string;
    /** Every client pause ends "See credits →"; staff copy carries none. */
    link: typeof SEE_CREDITS_LINK | null;
}

function orgOr(ctx: PauseContext, fallback: string): string {
    return ctx.orgName ?? fallback;
}

function nextStep(ctx: PauseContext): string {
    if (ctx.audience === 'staff') return '';
    return askForCreditsLine({ orgName: ctx.orgName, isOrgAdmin: ctx.audience === 'admin' });
}

function join(...parts: string[]): string {
    return parts.filter(Boolean).join(' ');
}

/**
 * The copy for one refusal or pause. Out of credits, an ended trial and a trial not started are
 * worded differently on purpose (FT-6). A plan end is never called a licence end in client copy.
 */
export function pauseCopy(reason: PauseReason, ctx: PauseContext): PauseCopy {
    const staff = ctx.audience === 'staff';
    const link = staff ? null : SEE_CREDITS_LINK;
    const whose = staff ? orgOr(ctx, 'This org') : 'Your organization';

    switch (reason) {
        case 'no_credits':
        case 'annual_pool_exceeded':
            return {
                title: 'DANI is paused: no credits left',
                body: join(`${whose} has no credits available.`, nextStep(ctx)),
                link,
            };
        case 'insufficient_for_action': {
            const needs =
                ctx.needed !== undefined && ctx.available !== undefined
                    ? `This needs ${creditsAmount(ctx.needed)} and ${creditsAmount(Math.max(ctx.available, 0))} ${creditUnit(Math.max(ctx.available, 0)) === 'credit' ? 'is' : 'are'} available.`
                    : `${whose} doesn't have enough credits for this.`;
            return { title: 'Not enough credits', body: join(needs, nextStep(ctx)), link };
        }
        case 'user_spend_limit_exceeded': {
            // D26: per-person limits apply only to MFour's own orgs, so whoever reads this is MFour
            // staff; a client user is never refused for one, so no client next step.
            const limit = ctx.limit !== undefined ? ` of ${creditsAmount(ctx.limit)}` : '';
            const reset = ctx.resetDate ? ` It resets on ${creditDate(ctx.resetDate)}.` : '';
            const subject = staff ? 'This person has' : "You've";
            return {
                title: 'Monthly limit reached',
                body: join(
                    `${subject} reached the monthly limit${limit}.${reset}`,
                    staff ? '' : STAFF_LIMIT_NEXT_STEP,
                ),
                link,
            };
        }
        case 'license_expired':
            return {
                title: 'DANI is paused: the plan has ended',
                body: join(
                    ctx.date
                        ? `${whose}'s Studio plan ended on ${creditDate(ctx.date)}.`
                        : `${whose}'s Studio plan has ended.`,
                    nextStep(ctx),
                ),
                link,
            };
        case 'trial_ended':
            return {
                title: staff ? 'The trial has ended' : 'Your trial has ended',
                body: join(
                    ctx.date
                        ? `The trial ended on ${creditDate(ctx.date)}.`
                        : 'The trial has ended.',
                    staff ? '' : `To keep using DANI, contact ${contactLine()}.`,
                ),
                link,
            };
        case 'trial_not_started':
            return {
                title: staff ? "The trial hasn't started yet" : "Your trial hasn't started yet",
                body: ctx.date
                    ? `The trial starts on ${creditDate(ctx.date)}.`
                    : "The trial hasn't started yet.",
                link,
            };
        case 'not_entitled':
            return {
                title: 'Not included in the plan',
                body: join(
                    `This isn't included in ${staff ? `${orgOr(ctx, 'this org')}'s` : 'your'} plan.`,
                    nextStep(ctx),
                ),
                link,
            };
        case 'monthly_cap_exceeded':
            // Legacy orgs only, until the cleanup PR (§7 "Chat"): keeps its reset date.
            return {
                title: ctx.resetDate
                    ? `DANI is paused until ${creditDate(ctx.resetDate)}: this month's DANI allowance is used up`
                    : "DANI is paused: this month's DANI allowance is used up",
                body: staff
                    ? `${orgOr(ctx, 'This org')} has used this month's DANI allowance. Every plan pauses there until the reset.`
                    : nextStep(ctx),
                link,
            };
        case 'LEDGER_NOT_LIVE':
            return {
                title: 'Not on the new credits balance yet',
                body: `${orgOr(ctx, 'This org')} isn't on the new credits balance yet, so credits can't be moved to or from it.`,
                link: null,
            };
        default: {
            const unreachable: never = reason;
            return { title: String(unreachable), body: '', link };
        }
    }
}

// ---------------------------------------------------------------------------------------------
// The pre-check
// ---------------------------------------------------------------------------------------------

/**
 * Titles for a pre-check's "no" on anything but chat (§6 "Pre-check"): a feature asking before
 * it starts is not DANI pausing, so the chat pause's "DANI is paused" titles give way here. The
 * bodies are the pause's own.
 */
const PRE_CHECK_TITLES: Partial<Record<PauseReason, string>> = {
    no_credits: 'No credits left',
    annual_pool_exceeded: 'No credits left',
    license_expired: 'The plan has ended',
    monthly_cap_exceeded: "This month's DANI allowance is used up",
};

function isPauseReason(value: unknown): value is PauseReason {
    return typeof value === 'string' && (PAUSE_REASONS as readonly string[]).includes(value);
}

/**
 * The copy for a pre-check's "no" (`POST /credit-consumption/spend-check`, M4S-5912 §6), or null
 * when the answer is yes. Every reason reads as its pause does (`pauseCopy`), with the figures the
 * pre-check returned: what the action needs and what is available, the plan or trial end, and the
 * reset date. A chat pre-check reads exactly as the chat's own pause. A reason this copy does not
 * know reads as no credits, never as a yes.
 */
export function spendCheckCopy(
    purpose: SpendPurpose,
    check: Pick<
        SpendCheckResult,
        'allowed' | 'reason' | 'available' | 'needed' | 'endDate' | 'resetDate'
    >,
    ctx: Pick<PauseContext, 'audience' | 'orgName' | 'limit'>,
): PauseCopy | null {
    if (check.allowed) return null;
    const reason: PauseReason = isPauseReason(check.reason) ? check.reason : 'no_credits';
    const copy = pauseCopy(reason, {
        ...ctx,
        available: check.available,
        needed: check.needed,
        resetDate: check.resetDate,
        // The end date is the plan's or the trial's; a trial not yet started is named without one.
        date: reason === 'trial_not_started' ? null : check.endDate,
    });
    const title = purpose === 'chat' ? undefined : PRE_CHECK_TITLES[reason];
    return title ? { ...copy, title } : copy;
}

// ---------------------------------------------------------------------------------------------
// Pace
// ---------------------------------------------------------------------------------------------

export interface PaceInput {
    /** Days of usage the pace is measured over. */
    basisDays: number;
    /** Average credits per week over the basis. */
    perWeek: number;
    /** When the balance runs out at this pace; null when it does not (no usage). */
    runsOutOn: string | null;
    /** True when the run-out date falls before the contract year ends. */
    runsOutBeforeEnd: boolean;
    /** Current available credits, for the chat warning. */
    available: number;
}

export interface PaceCopy {
    line: string;
    /** "Runs out before your contract year ends", when it does. */
    beforeEnd: string | null;
    /** The chat's dismissible warning; shown only when the API's pace warns (within 30 days). */
    warning: string;
}

export function paceCopy(pace: PaceInput): PaceCopy {
    const perWeek = `about ${formatCredits(Math.round(pace.perWeek))} a week`;
    const line = pace.runsOutOn
        ? `At your pace over the last ${pace.basisDays} days (${perWeek}), your balance lasts until about ${creditDate(pace.runsOutOn)}`
        : `At your pace over the last ${pace.basisDays} days (${perWeek}), your balance does not run out`;
    return {
        line,
        beforeEnd: pace.runsOutBeforeEnd ? 'Runs out before your contract year ends' : null,
        warning: pace.runsOutOn
            ? `Credits are running low: about ${creditsAmount(Math.max(pace.available, 0))} left, enough until about ${creditDate(pace.runsOutOn)}`
            : `Credits are running low: about ${creditsAmount(Math.max(pace.available, 0))} left`,
    };
}

// ---------------------------------------------------------------------------------------------
// Trials (the chat's trial strip, §7 "Chat")
// ---------------------------------------------------------------------------------------------

/** The upgrade link the retired trial banner carried (`TrialNotificationBanner`). */
export const TRIAL_UPGRADE_LINK = {
    label: 'Go pro to keep access to DANI',
    href: 'https://www.mfour.com/contact-us/',
} as const;

export interface TrialStripCopy {
    /** "120 trial credits left · trial ends Nov 30, 2026". */
    line: string;
    link: typeof SEE_CREDITS_LINK;
    upgrade: typeof TRIAL_UPGRADE_LINK;
}

/**
 * The strip under the chat input for a trial on credits, where the old message counter sat:
 * "{n} trial credits left · trial ends {date} · See credits", with the upgrade link. Below zero
 * reads as none left; the end date is left out when the trial has none.
 */
export function trialStripCopy(trial: {
    available: number;
    endDate: string | null;
}): TrialStripCopy {
    const left = Math.max(trial.available, 0);
    const amount = `${formatCredits(left)} trial ${creditUnit(left)} left`;
    const ends = trial.endDate ? ` · trial ends ${creditDate(trial.endDate)}` : '';
    return { line: `${amount}${ends}`, link: SEE_CREDITS_LINK, upgrade: TRIAL_UPGRADE_LINK };
}

// ---------------------------------------------------------------------------------------------
// Account status and the fleet (staff, §7 "CS org page" item 3 and "Fleet")
// ---------------------------------------------------------------------------------------------

/**
 * One account's status on the fleet's Status column and the CS page's status strip, in the
 * fleet's attention-first order: paused, below zero, runs out before renewal, low usage, on pace,
 * and "No credit limit" last. Which status a balance has is `creditStatusOf`
 * (`src/@credit-consumption/utils/fleet-balance.ts`); the words are here.
 */
export const CREDIT_STATUSES = [
    'paused',
    'below_zero',
    'runs_out_early',
    'low_usage',
    'on_pace',
    'no_limit',
] as const;

export type CreditStatus = (typeof CREDIT_STATUSES)[number];

/** A pause reason, short, after "Paused: " (staff). */
const PAUSED_SHORT: Record<PauseReason, string> = {
    no_credits: 'no credits left',
    annual_pool_exceeded: 'no credits left',
    insufficient_for_action: 'not enough credits',
    user_spend_limit_exceeded: 'monthly limit reached',
    license_expired: 'plan ended',
    trial_ended: 'trial ended',
    trial_not_started: "trial hasn't started",
    not_entitled: 'not in the plan',
    monthly_cap_exceeded: "this month's DANI allowance is used up",
    LEDGER_NOT_LIVE: 'not on the new credits balance yet',
};

/**
 * "Paused: no credits left", "Below zero", "Runs out before renewal", "Low usage", "On pace",
 * "No credit limit" ("No credit limit (MFour)" on MFour's own orgs). Staff only.
 */
export function creditStatusLabel(
    status: CreditStatus,
    ctx: { reason?: string | null; internal?: boolean } = {},
): string {
    switch (status) {
        case 'paused': {
            const short = ctx.reason ? PAUSED_SHORT[ctx.reason as PauseReason] : undefined;
            return short ? `Paused: ${short}` : 'Paused';
        }
        case 'below_zero':
            return CREDIT_TERMS.belowZero;
        case 'runs_out_early':
            return 'Runs out before renewal';
        case 'low_usage':
            return 'Low usage';
        case 'on_pace':
            return 'On pace';
        case 'no_limit':
            return noLimitLabel(Boolean(ctx.internal));
        default: {
            const unreachable: never = status;
            return String(unreachable);
        }
    }
}

/** "New balance" / "Previous calculation": the fleet's Source cell (its tooltip is `sourceCaption`). */
export function sourceLabel(reader: 'ledger' | 'legacy'): string {
    return reader === 'ledger' ? 'New balance' : 'Previous calculation';
}

/** The fleet's columns, on screen and as the CSV headers (§7 "Fleet"). */
export const FLEET_COLUMNS = {
    organization: 'Organization',
    owner: 'Owner',
    csm: 'CSM',
    plan: 'Plan',
    balance: CREDIT_TERMS.balance,
    used: 'Used',
    runsOut: 'Runs out',
    contractYearEnds: 'Contract year ends',
    status: 'Status',
    source: 'Source',
    expiredUnused: 'Expired unused',
} as const;

/** Booked reports and surveys, as a fleet CSV column (a usage purpose a client sees as two rows). */
const BOOKING_COLUMN = 'Booked by MFour (reports and surveys)';

/**
 * A usage purpose as a fleet CSV column: the purposes' own names (with "DANI work not tied to one
 * conversation"), bookings, and any other purpose by its key.
 */
export function fleetPurposeColumn(purpose: string): string {
    if (purpose === 'booking') return BOOKING_COLUMN;
    if (
        purpose === 'chat' ||
        purpose === 'unattributed' ||
        purpose === 'report' ||
        purpose === 'feasibility' ||
        purpose === 'survey_build' ||
        purpose === 'fielding' ||
        purpose === 'model_call'
    ) {
        return purposeLabel(purpose);
    }
    return purpose;
}

/** "12 accounts · 1,234 total balance · 456 used in period · 3 need attention". */
export function fleetSummaryLine(s: {
    accounts: number;
    totalBalance: number;
    usedInPeriod: number;
    needAttention: number;
}): string {
    const accounts = `${s.accounts.toLocaleString()} ${s.accounts === 1 ? 'account' : 'accounts'}`;
    const total = `${s.totalBalance < 0 ? LEDGER_MINUS : ''}${formatCredits(Math.abs(s.totalBalance))} total balance`;
    const used = `${formatCredits(s.usedInPeriod)} used in period`;
    const attention = `${s.needAttention.toLocaleString()} ${s.needAttention === 1 ? 'needs' : 'need'} attention`;
    return [accounts, total, used, attention].join(' · ');
}

/** Empty "My accounts": the line and the button that shows every account. */
export const FLEET_EMPTY_MINE = {
    line: 'No accounts where you are the owner or CSM.',
    showAll: 'Show all accounts',
} as const;

// ---------------------------------------------------------------------------------------------
// Fielding
// ---------------------------------------------------------------------------------------------

/**
 * The launcher's quote (§6): "This survey holds 500 credits (5 per complete × 100). You're
 * charged per complete; unused credits come back when fielding closes."
 */
export function fieldingQuoteCopy(q: { unitCredits: number; target: number }): string {
    const held = q.unitCredits * q.target;
    return `This survey holds ${creditsAmount(held)} (${formatCredits(q.unitCredits)} per complete × ${q.target.toLocaleString()}). You're charged per complete; unused credits come back when fielding closes.`;
}

// ---------------------------------------------------------------------------------------------
// CS actions (staff only: M4S-5911, §5, plan step 23)
// ---------------------------------------------------------------------------------------------

/**
 * The words on the CS action buttons and dialogs. What the client reads about a CS row is
 * `rowLabel` with the row's client text; the reason list and its client labels come from the API
 * (`GET admin/credit-consumption/adjustment-reasons`), never from here.
 */
export const CS_ACTION_TERMS = {
    adjust: 'Adjust credits',
    add: 'Add credits',
    remove: 'Remove credits',
    move: 'Move credits',
    extend: 'Extend',
    reverse: 'Reverse',
    reversed: 'Reversed',
    /** An action the viewer cannot take stays visible, disabled, with this (§5). */
    needsAdmin: 'Needs an admin',
    /** The API refused the action for a missing right (403 `NEEDS_ADMIN`). */
    needsAdminRefused: 'This change needs an admin. Ask an admin to make it.',
    /** Every CS action is a staff write, refused under View-as (§5 "View-as"). */
    clearViewAs: 'Clear View as to adjust credits',
    review: 'Review',
    back: 'Back',
    internalReasonHelp: 'Internal only, at least 10 characters. The client never sees it.',
    ledgerWritesOff:
        'Ledger writes are switched off in this environment, so no ledger row was written.',
} as const;

/** "The client sees: Adjustment by MFour · Credit for a failed run" (the confirm step). */
export function clientSeesLine(label: string): string {
    return `The client sees: ${label}`;
}

/**
 * D23: credits moved to an org make it a paid plan for Studio access, but DANI access is still
 * granted in DANI Access, so the move confirm warns when the target has none.
 */
export function daniAccessWarning(orgName: string): string {
    return `${orgName} has no DANI access. These credits make it a paid plan for Studio, but DANI is granted separately in DANI Access.`;
}

/** D14: the author of an add may undo it without an admin until this time. */
export function undoUntilLine(formattedTime: string): string {
    return `You can reverse your own add until ${formattedTime} without an admin.`;
}

// ---------------------------------------------------------------------------------------------
// CS org page (staff only: plan step 28, §7 "CS org page")
// ---------------------------------------------------------------------------------------------

/** The CS org page's fixed staff words. */
export const CS_ORG_TERMS = {
    noPlan: 'No plan',
    exportCsv: 'Export',
    exportClient: 'Client copy',
    exportClientHelp: "Exactly the client's own activity export",
    exportInternal: 'Internal copy',
    exportInternalHelp:
        "Adds the actor, internal reason, counterparty and dollars at each row's date",
    pullFromSalesforce: 'Pull from Salesforce',
    pullFromSalesforceHelp:
        'Posts new and changed contracts, packs and bookings; never changes an adjustment',
    settings: 'Settings',
    whatToTellClient: 'What to tell the client',
    atLimit: 'At limit',
    giveBack: 'Give back for this row',
    interimFixes: 'Interim fixes now covered by Salesforce',
    interimCovered: 'Now in Salesforce',
    interimWaiting: 'Not in Salesforce yet',
    interimUnchecked: 'Not checked: more activity than one read covers',
    droppedRecords: 'Records Salesforce dropped are logged by the sync, not listed here yet.',
} as const;

/** §7 CS page item 4: the staff caption under the client's balance. */
export function csBalanceCaption(orgName: string): string {
    return `${orgName} sees this balance. DANI pauses when available credits (balance minus holds and usage not counted yet) reach 0.`;
}

/** "Salesforce last pulled {time}" (the header), or that it never was. */
export function salesforcePulledLine(formattedTime: string | null): string {
    return formattedTime
        ? `Salesforce last pulled ${formattedTime}`
        : 'Not pulled from Salesforce yet';
}
